import { supabase } from './supabaseClient';
import { fetchWithRetry, getContextHeaders } from './httpClient';

// Adresse de l API.
//
// REACT_APP_API_URL accepte une valeur RELATIVE, « /api », et c est la plus
// sure quand le meme serveur sert le site et l API : le fichier construit
// n embarque alors aucun nom de machine et fonctionne partout — sur une IP
// aujourd hui, sur le domaine demain, sans reconstruction.
//
// Une adresse absolue reste necessaire la ou site et API sont deux services
// distincts (Render) ou sur deux ports (developpement local).
//
// Le 2026-09-19, un fichier construit sans cette variable a ete deploye sur
// Scaleway : le site y appelait l API de Render, et l espace d administration
// repondait « Page introuvable » alors que tout etait correctement configure.
// Une adresse d API valable commence par http(s):// ou par une barre. Tout
// le reste est une valeur abimee en chemin, et le site est alors MORT sans
// rien dire : chaque appel echoue en « Failed to fetch », les boutons ne
// repondent plus, les pages se vident.
//
// Ca n a rien de theorique. Le 2026-09-19, une construction lancee depuis
// Git Bash avec REACT_APP_API_URL=/api a livre un site qui appelait
// file:///C:/Program Files/Git/api/health : MSYS convertit toute valeur
// commencant par une barre en chemin Windows. Le fichier construit etait
// pourtant conforme a tous les controles qu'on lui faisait passer.
//
// On retombe donc sur une adresse relative, et on le CRIE dans la console :
// un site qui marche vaut mieux qu un site mort, mais la construction est a
// refaire.
const ADRESSE_VALABLE = /^(https?:\/\/|\/)/;

const buildApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  if (!ADRESSE_VALABLE.test(configured)) {
    // eslint-disable-next-line no-console
    console.error(
      `[Bookipix] REACT_APP_API_URL vaut « ${configured} », ce qui n'est pas une adresse. `
      + 'Le site a ete construit avec une variable abimee (souvent MSYS/Git Bash qui '
      + 'transforme /api en chemin Windows : utiliser MSYS_NO_PATHCONV=1). '
      + 'On se rabat sur /api ; la construction est a refaire.'
    );
    return '/api';
  }

  const trimmed = configured.replace(/\/$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};
// Timeouts/retry mutualises (voir services/httpClient.js) — 15s codes en
// dur ici auparavant, trop court pour le reveil d'une instance Render.

const buildHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('Session invalide. Merci de vous reconnecter.');
  }
  return { token, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } };
};

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_error) {
    return {};
  }
};

// Requete JSON standard (GET/POST/PUT/DELETE avec corps JSON). Timeout +
// seconde tentative sur reveil d'instance : voir httpClient.js.
const request = async (path, options = {}) => {
  const { headers } = await buildHeaders();
  const response = await fetchWithRetry(`${buildApiBaseUrl()}${path}`, {
    ...options,
    headers: { ...headers, ...(options.headers || {}) }
  });

  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    const error = new Error(payload?.error || 'Erreur du moteur de mise en page.');
    // Details structures conserves, pas seulement le message : certaines
    // routes repondent autre chose qu'un simple echec (ex. POST /pages/shrink
    // renvoie 409 + needsConfirmation + les numeros des pages concernees,
    // pour que l'appelant puisse POSER la question plutot que d'afficher un
    // refus sec). Purement additif : les appelants existants continuent de
    // lire err.message comme avant.
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
};

// Requete "brute" (pas de Content-Type: application/json impose) : utilisee
// pour l'upload multipart et pour recuperer l'apercu (HTML texte / PDF blob),
// deux cas ou request() ne convient pas.
const rawRequest = async (path, options = {}) => {
  const { token } = await buildHeaders();
  const response = await fetch(`${buildApiBaseUrl()}${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...getContextHeaders(), ...(options.headers || {}) }
  });
  if (!response.ok) {
    const payload = await parseJsonSafe(response);
    const error = new Error(payload?.error || `Erreur (${response.status}).`);
    // Code stable (ex. 'photo_too_small') et statut : l'appelant peut distinguer un refus precis d'une panne.
    error.status = response.status;
    error.code = payload?.code;
    throw error;
  }
  return response;
};

// Requete publique (aucune session requise) : pour les pages accessibles
// avant connexion, comme l'entree de creation depuis la page d'accueil.
const publicRequest = async (path, options = {}) => {
  const response = await fetchWithRetry(`${buildApiBaseUrl()}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) }
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    throw new Error(payload?.error || 'Erreur.');
  }
  return payload;
};

// Requete publique "brute" (aucune session requise, pas de Content-Type
// impose) : pour l'upload multipart de la page de contribution sans compte
// (voir /participer/:token) — meme principe que rawRequest, sans l'exigence
// d'authentification.
const publicRawRequest = async (path, options = {}) => {
  const response = await fetch(`${buildApiBaseUrl()}${path}`, {
    ...options,
    headers: { ...getContextHeaders(), ...(options.headers || {}) }
  });
  if (!response.ok) {
    const payload = await parseJsonSafe(response);
    const error = new Error(payload?.error || `Erreur (${response.status}).`);
    // Code stable (ex. 'photo_too_small') et statut : l'appelant peut distinguer un refus precis d'une panne.
    error.status = response.status;
    error.code = payload?.code;
    throw error;
  }
  return response;
};

// Envoi d'un fichier public AVEC suivi de progression (barre de progression des invites, 2026-10-23) : fetch ne sait pas
// suivre l'envoi, XMLHttpRequest si. Memes erreurs que publicRawRequest (message, status, code). onProgress(fraction 0..1).
const publicUploadWithProgress = (path, formData, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${buildApiBaseUrl()}${path}`);
  Object.entries(getContextHeaders() || {}).forEach(([cle, valeur]) => { if (valeur !== undefined && valeur !== null) xhr.setRequestHeader(cle, String(valeur)); });
  if (onProgress && xhr.upload) {
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
  }
  xhr.onload = () => {
    let payload = null;
    try { payload = JSON.parse(xhr.responseText); } catch (_e) { payload = null; }
    if (xhr.status >= 200 && xhr.status < 300) { if (onProgress) onProgress(1); resolve(payload); return; }
    const error = new Error(payload?.error || `Erreur (${xhr.status}).`);
    error.status = xhr.status;
    error.code = payload?.code;
    reject(error);
  };
  xhr.onerror = () => reject(new Error('Connexion interrompue.'));
  xhr.send(formData);
});

// --- Creation d'un livre (occasion + titre, sans IA) -------------------------

// Catalogue des occasions (public, donnees pures — pas d'appel IA).
export const listEventTypes = () => publicRequest('/books/create/event-types');

// Cree un livre nu (titre + occasion), sans passer par l'assistant IA
// (backend/routes/bookCreation.js). Reutilise le CRUD generique existant.
export const createBook = (payload) => request('/books', {
  method: 'POST',
  body: JSON.stringify(payload)
});

// --- Catalogues (publics, pas d'auth requise) -------------------------------

export const listTemplates = () => request('/catalog/templates');
export const listLayouts = () => request('/catalog/layouts');
// listProducts (book_products) retire (chantier "tarification dynamique",
// 2026-09-27) : cette table etait deja confirmee morte (price_cents=0/
// active=false partout, sa route backend meme pas montee dans server.js,
// aucun appelant frontend) — voir services/pricing/ pour la vraie grille.

// --- Contenu d'un livre ------------------------------------------------------

export const listContentItems = (bookId) => request(`/books/${bookId}/content-items`);

// Mode Automatique (§06) : palier de pages recommande a partir du contenu reel.
export const getRecommendedPageCount = (bookId) => request(`/books/${bookId}/recommended-page-count`);

// Modifie un souvenir existant (edition du texte directement sur la page,
// voir AtelierTextEditor.js) — backend PUT /content-items/:itemId.
export const updateContentItem = (bookId, itemId, patch) => request(`/books/${bookId}/content-items/${itemId}`, {
  method: 'PUT',
  body: JSON.stringify(patch)
});

// `origin` dit d'ou vient le souvenir, et decide de sa duree de vie (voir
// backend routes/composition.js sanitizeItemOrigin) :
//   'library' — ajoute deliberement via le bouton "Ajouter" : il reste, qu'il
//               soit utilise ou non. C'est le defaut.
//   'page'    — ecrit directement dans un emplacement de page : il n'existe
//               que pour cet emplacement, et disparait s'il en sort.
export const addTextItem = (bookId, text, displayOrder = 0, origin = 'library') => request(`/books/${bookId}/content-items`, {
  method: 'POST',
  body: JSON.stringify({
    source: 'upload',
    kind: 'texte',
    text,
    display_order: displayOrder,
    metadata: { origin: origin === 'page' ? 'page' : 'library' }
  })
});

// Supprime TOUS les souvenirs d'un type ('photo' ou 'texte'). Irreversible :
// le backend exige `confirm: true` et nettoie aussi les references laissees
// sur les pages ainsi que les fichiers du stockage.
export const deleteAllContentItems = (bookId, kind) => request(
  `/books/${bookId}/content-items?kind=${encodeURIComponent(kind)}`,
  { method: 'DELETE', body: JSON.stringify({ confirm: true }) }
);

export const deleteContentItem = (bookId, itemId) => request(`/books/${bookId}/content-items/${itemId}`, {
  method: 'DELETE'
});

export const uploadPhoto = async (bookId, file, displayOrder = 0) => {
  const formData = new FormData();
  formData.append('photo', file);
  formData.append('display_order', String(displayOrder));

  const response = await rawRequest(`/books/${bookId}/content-items/photo`, {
    method: 'POST',
    body: formData
  });
  return response.json();
};

// --- Livre (template / nombre de pages) --------------------------------------

// ACCES AUX LIVRES PAR L'API (chantier « hors Supabase », 2026-10-18) : ces fonctions REMPLACENT les lectures/ecritures que
// les ecrans faisaient directement dans la base avec la cle publique. Elles rendent la meme forme que ce que renvoyait le
// client de base ({ data, error }), pour que les ecrans changent le moins possible. Le controle du proprietaire est fait
// par le serveur (routes/books.js).
const asResult = async (promise) => {
  try {
    return { data: await promise, error: null };
  } catch (error) {
    // Code d'erreur structure du serveur (ex. 23505 = deja present), lisible comme avec le client de base.
    if (error && error.payload && error.payload.code && !error.code) error.code = error.payload.code;
    return { data: null, error };
  }
};

// Ligne du livre. « bookEditBypassed » (ajoute par le serveur pour le bandeau « mode test ») est retire : les ecrans lisaient
// jusqu'ici la ligne brute de la base, sans ce champ, et gardent exactement ce comportement.
export const fetchBookRow = (bookId) => asResult(
  request(`/books/${bookId}`).then(({ bookEditBypassed: _ignore, ...row }) => row)
);

// Mise a jour du livre ; le serveur refuse (423) sur un livre deja paye.
export const updateBookRow = (bookId, updates) => asResult(request(`/books/${bookId}`, {
  method: 'PUT',
  body: JSON.stringify(updates)
}));

export const listMyBooks = () => asResult(request('/books'));
export const getDashboardData = () => request('/books/dashboard');
export const getLatestBookWithPhoto = () => request('/books/latest-with-photo');
export const archiveBookById = (bookId, months) => asResult(request(`/books/${bookId}/archive`, { method: 'POST', body: JSON.stringify({ months }) }));
export const restoreBookById = (bookId) => asResult(request(`/books/${bookId}/restore`, { method: 'POST' }));
export const deleteBookById = (bookId) => asResult(request(`/books/${bookId}`, { method: 'DELETE' }));
export const listBookContributors = (bookId) => asResult(request(`/books/${bookId}/contributors`));
export const addBookContributor = (bookId, email, name) => asResult(request(`/books/${bookId}/contributors`, { method: 'POST', body: JSON.stringify({ email, name }) }));
export const removeBookContributor = (bookId, contributorId) => asResult(request(`/books/${bookId}/contributors/${contributorId}`, { method: 'DELETE' }));
export const fetchChaptersFull = (bookId) => asResult(request(`/chapters/book/${bookId}/full`));

export const updateBook = (bookId, payload) => request(`/books/${bookId}`, {
  method: 'PUT',
  body: JSON.stringify(payload)
});

// Estimation de prix en lecture seule (jamais persistee) : printFormat/
// pageCount, si fournis, surchargent les valeurs reelles du livre pour la
// simulation — utilise par Configuration pour afficher le prix de chaque
// type d'album avant meme de le choisir. Vit sous /orders (pas /books) :
// meme routeur que la creation de commande, computeOrderPricing y est
// deja defini (backend/routes/orders.js), jamais une deuxieme
// implementation du calcul de prix.
// type/quantity optionnels ('print'/1 par defaut cote backend si omis) :
// simulent le VRAI prix d'un type de commande (pdf/print/pack) et d'une
// quantite donnee — reponse `{ printFormat, pageCount, unitCents, totalCents }`.
// `destination` (2026-11-03) : le pays de l'adresse de livraison deja saisie — c'est lui qui decidera du port a la commande, il
// prime donc sur le pays choisi dans l'en-tete.
export const estimatePrice = (bookId, { printFormat, pageCount, type, quantity, promoCode, destination } = {}) => {
  const params = new URLSearchParams();
  if (destination) params.set('destination', String(destination));
  if (printFormat) params.set('print_format', printFormat);
  if (pageCount) params.set('page_count', String(pageCount));
  if (type) params.set('type', type);
  if (quantity) params.set('quantity', String(quantity));
  if (promoCode) params.set('promoCode', String(promoCode));
  const query = params.toString();
  return request(`/orders/book/${bookId}/price-estimate${query ? `?${query}` : ''}`);
};

// --- Composition + apercu -----------------------------------------------------

// `mood` (optionnel) : ambiance de composition choisie dans l'atelier (voir
// atelierMoods.js) — jamais persistee, simple parametre de generation
// ponctuel (comme `variant`). Omise = comportement standard (l'assistant
// /composer existant continue d'appeler composeBook sans mood, inchange).
export const composeBook = (bookId, variant = 0, mood) => request(`/books/${bookId}/compose`, {
  method: 'POST',
  body: JSON.stringify({ variant, ...(mood ? { mood } : {}) })
});

export const fetchPreviewHtml = async (bookId) => {
  const response = await rawRequest(`/books/${bookId}/preview.html`);
  return response.text();
};

// Apercu dedie a UNE face de la couverture (front|back), sans les pages
// interieures : rapide, cale sur width:100vw/height:100vh cote serveur donc
// jamais de defilement quelle que soit la taille de la iframe qui l'affiche
// — utilise par l'ecran de personnalisation legere de la couverture (photo,
// sous-titre, date, phrase de 4e).
// options.placeholders : apercu de l'atelier — un style avec photo sans photo garde sa forme et montre
// « + Ajouter une photo » (voir backend coverComposer ; jamais dans le PDF / l'Apercu final).
export const fetchCoverPreviewHtml = async (bookId, face = 'front', options = {}) => {
  const extra = (options.placeholders ? `&placeholders=1&lang=${options.lang === 'en' ? 'en' : 'fr'}` : '')
    + (options.format ? `&format=${options.format}` : '')
    + (options.style ? `&style=${options.style}` : '');
  const response = await rawRequest(`/books/${bookId}/cover-preview.html?face=${face === 'back' ? 'back' : 'front'}${extra}`);
  return response.text();
};

// Couverture d'EXEMPLE pour l'ecran de choix du format (public, aucune session) : meme rendu que l'atelier.
// style : 'full' | 'frame' | 'type' ; format : 'livret' | 'standard' | 'luxe'.
export const fetchCoverSampleHtml = async ({ format, style, lang }) => {
  const query = new URLSearchParams({ format, style, lang: lang === 'en' ? 'en' : 'fr' });
  // Jeton joint s'il existe : un produit reserve aux testeurs n'est rendu qu'a son compte.
  let headers = {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers = { Authorization: `Bearer ${session.access_token}` };
  } catch (_error) { /* apercu public */ }
  const response = await publicRawRequest(`/catalog/cover-sample.html?${query.toString()}`, { headers });
  return response.text();
};

// Page d'INTERIEUR d'exemple (public) : side 'left' | 'right' = les deux pages d'une double page, a afficher cote a cote.
// Un format layflat montre une photo panoramique continue sur les deux ; les autres, une page photo + une page de deux photos.
export const fetchPageSampleHtml = async ({ format, side, lang }) => {
  const query = new URLSearchParams({ format, side: side === 'right' ? 'right' : 'left', lang: lang === 'en' ? 'en' : 'fr' });
  let headers = {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers = { Authorization: `Bearer ${session.access_token}` };
  } catch (_error) { /* apercu public */ }
  const response = await publicRawRequest(`/catalog/page-sample.html?${query.toString()}`, { headers });
  return response.text();
};

export const fetchPreviewPdfBlob = async (bookId) => {
  const response = await rawRequest(`/books/${bookId}/preview.pdf`);
  return response.blob();
};

// --- Apercu multi-format (JE VERIFIE) -----------------------------------------
// Voir backend/services/composition/formatComposer.js : le contenu verrouille
// dans l'atelier reste identique, seul le contenu automatique est recompose
// avec la densite propre a chaque format (marges/photos/typo/mise en page).

// Pagination reelle de CHAQUE format (livret/standard/luxe) pour le contenu
// actuel, sans rien persister — alimente les 3 cartes de l'Apercu final avant
// meme un clic. Reponse : { formats: [{ formatId, pageCount }, ...] }.
export const getFormatOptions = (bookId) => request(`/books/${bookId}/format-options`);

// Choisit un format : recompose (contenu non verrouille seulement),
// persiste, et met a jour book.print_format/book.page_count — ce que
// "Commander mon livre" imprimera correspond donc toujours a ce qui vient
// d'etre recompose ici. Reponse : { book, pages, pageCount }.
// N'a plus d'appelant depuis le chantier "tarification dynamique"
// (2026-09-27) — voir changeFormatOnly ci-dessous — mais laisse en place
// (fonctionne toujours) au cas ou un usage futur en aurait besoin.
export const chooseFormat = (bookId, formatId) => request(`/books/${bookId}/format`, {
  method: 'POST',
  body: JSON.stringify({ formatId })
});

// Change UNIQUEMENT print_format (retour utilisateur, 2026-09-27, §9 :
// "changer de format ne doit PAS renvoyer l'utilisateur dans l'atelier...
// seul le produit physique et le prix changent") — aucune recomposition,
// aucun changement de pagination/contenu, contrairement a chooseFormat
// ci-dessus. Reponse : { book }.
export const changeFormatOnly = (bookId, formatId) => request(`/books/${bookId}/format-only`, {
  method: 'POST',
  body: JSON.stringify({ formatId })
});

// Ajoute des pages vides a la fin du livre (bouton "+2" du filmstrip
// atelier) — voir routes/composition.js: POST /pages/extend.
export const extendBookPages = (bookId, count = 2) => request(`/books/${bookId}/pages/extend`, {
  method: 'POST',
  body: JSON.stringify({ count })
});

// Retire des pages a la fin du livre (bouton "-2" du filmstrip atelier) —
// voir routes/composition.js: POST /pages/shrink. `confirm` n'est a passer
// que si le serveur a repondu 409 needsConfirmation (les pages retirees ne
// sont pas vides) ET que l'utilisateur a explicitement accepte la perte.
export const shrinkBookPages = (bookId, count = 2, confirm = false) => request(`/books/${bookId}/pages/shrink`, {
  method: 'POST',
  body: JSON.stringify({ count, confirm })
});

// Deplace une page a une autre position (glisser-deposer dans le filmstrip) —
// voir routes/composition.js: POST /pages/move. Ce n'est pas une permutation :
// les pages situees entre les deux positions se decalent, l'ordre de lecture
// du reste du livre est donc preserve.
export const movePage = (bookId, fromIndex, toIndex) => request(`/books/${bookId}/pages/move`, {
  method: 'POST',
  body: JSON.stringify({ fromIndex, toIndex })
});

// --- Atelier de creation personnalisee (edition manuelle page par page) -----

export const listPages = (bookId) => request(`/books/${bookId}/pages`);

// Apercu d'UNE page interieure a la fois, contenu reel — meme moteur que le
// PDF final (renderSinglePageHtml, voir routes/composition.js), meme
// principe que fetchCoverPreviewHtml pour les couvertures. Fonctionne aussi
// pour une page jamais encore construite (rendue comme une page vide).
export const fetchInteriorPagePreviewHtml = async (bookId, pageIndex) => {
  const response = await rawRequest(`/books/${bookId}/pages/${pageIndex}/preview.html`);
  return response.text();
};

// Sauvegarde validee d'une page construite a la main : { layoutId, itemIds }
// dans l'ordre des emplacements du format choisi. Toujours verrouillee
// (locked=true) cote serveur — jamais ecrasee par une recomposition
// automatique ulterieure (voir manualPageBuilder.js).
// photoAdjustments (optionnel, cahier des charges "PhotoSlot" 2026-09-10) :
// { [itemId]: {focalX, focalY, zoom, fitMode} } — l'UI dediee pour le
// regler a la main a ete retiree (retour utilisateur : "ne sert a rien"),
// mais le champ reste transmis tel quel (toujours vide en pratique
// desormais) pour ne jamais ecraser silencieusement un ajustement deja
// enregistre. Nettoye/borne cote backend (routes/composition.js:
// sanitizePhotoAdjustments), jamais besoin de validation ici.
// textRoles/textStyles (optionnels, cahier des charges typographique
// 2026-09-11) : { [itemId]: 'title'|'subtitle'|'body'|'caption'|'quote' } et
// { [itemId]: {align, color, sizePt} }. Egalement nettoyes/bornes cote
// backend (sanitizeTextRoles/sanitizeTextStyles) : une valeur hors du cadre
// est ecartee au profit de celle du role, jamais appliquee telle quelle.
// `slotIds` + `framing` : cadrage PAR EMPLACEMENT (phase 4a). `photoAdjustments` :
// ancien format, encore accepte (le serveur le regenere a partir de framing).
export const saveManualPage = (bookId, pageIndex, {
  layoutId, itemIds, slotIds, framing, photoAdjustments, photoCaptions, textRoles, textStyles
}) => request(
  `/books/${bookId}/pages/${pageIndex}/manual`,
  { method: 'PUT', body: JSON.stringify({ layoutId, itemIds, slotIds, framing, photoAdjustments, photoCaptions, textRoles, textStyles }) }
);

// Sauvegarde d'une page "SANS EMPLACEMENT" (phase31, parite Exemples,
// 2026-10-11 : dedicace/chapitre manuel/remerciements) — route soeur de
// saveManualPage, mais sans layoutId/itemIds : `kind` + du texte libre (+
// une photo pour le chapitre). Voir routes/composition.js PUT .../manual-kind
// pour la validation (champs acceptes selon `kind`, photo verifiee comme
// appartenant au livre).
export const saveManualKindPage = (bookId, pageIndex, { kind, title, number, text, photoItemId }) => request(
  `/books/${bookId}/pages/${pageIndex}/manual-kind`,
  { method: 'PUT', body: JSON.stringify({ kind, title, number, text, photoItemId }) }
);

// Vide une page (retour a l'etat vierge, deverrouillee) : reutilise la route
// generique PUT /pages/:pageIndex deja existante, aucune route dediee
// necessaire pour "vider" une page.
export const clearPage = (bookId, pageIndex) => request(
  `/books/${bookId}/pages/${pageIndex}`,
  { method: 'PUT', body: JSON.stringify({ layout_id: null, content: {}, locked: false }) }
);

// Reecrit le contenu d'une page deja enregistree SANS repasser par
// saveManualPage (qui exige un nombre d'items EXACTEMENT egal au nombre
// d'emplacements du format et rejetterait donc un retrait partiel — voir
// backend/services/composition/manualPageBuilder.js) — reutilise la meme
// route generique que clearPage, avec un `content` deja recalcule cote
// appelant (ex. retirer un seul id de itemIds/blocks[*].itemIds). layoutId/
// locked sont TOUJOURS explicitement repasses (jamais omis) : cette route
// ecrit exactement ce qu'on lui donne, aucune fusion implicite a deviner.
export const updatePageContent = (bookId, pageIndex, { layoutId, content, locked }) => request(
  `/books/${bookId}/pages/${pageIndex}`,
  { method: 'PUT', body: JSON.stringify({ layout_id: layoutId, content, locked }) }
);

// Controle qualite d'impression (cahier des charges "PhotoSlot", 2026-09-10,
// §20/21) — utilise par AtelierFinishModal a l'ouverture ("Terminer mon
// livre"), un seul appel, jamais bloquant. { pagesCount, photosCount,
// lowQualityPhotos:[{pageIndex,itemId,level,dpi,emoji,label}], allGood }.
// --- Point de restauration avant generation automatique ---------------------
// Voir backend/sql/phase19_book_snapshots.sql. UN SEUL point par livre : le
// filet du dernier geste destructeur, pas un historique.
//
// `snapshot` vaut null quand il n'y a rien a retablir — ce n'est pas une
// erreur, c'est une reponse.
export const getBookSnapshot = (bookId) => request(`/books/${bookId}/snapshot`);

export const restoreBookSnapshot = (bookId) => request(
  `/books/${bookId}/snapshot/restore`,
  { method: 'POST' }
);

// « Je garde cette version » : abandonne le retour en arriere.
export const discardBookSnapshot = (bookId) => request(
  `/books/${bookId}/snapshot`,
  { method: 'DELETE' }
);

export const getPrintQualityCheck = (bookId) => request(`/books/${bookId}/print-quality-check`);

// « Dupliquer pour modifier » (atelier, livre verrouille apres paiement) :
// copie independante, non verrouillee, memes photos/textes/pages/couverture.
export const duplicateBook = (bookId) => request(`/books/${bookId}/duplicate`, { method: 'POST' });

// --- Lien de partage collaboratif (public, sans compte) ---------------------
// Voir backend/routes/composition.js (GET/POST /api/public/share/:token) —
// ce que le contributeur envoie ici atterrit directement dans
// book_content_items, visible immediatement dans l'atelier du proprietaire.

export const fetchShareInfo = (token) => publicRequest(`/public/share/${token}`);

export const submitShareText = (token, { text, contributorName, contributorEmail, contributionId }) => publicRequest(
  `/public/share/${token}/text`,
  { method: 'POST', body: JSON.stringify({ text, contributorName, contributorEmail, contributionId }) }
);

export const submitSharePhoto = async (token, file, { contributorName, contributorEmail, contributionId, onProgress }) => {
  const formData = new FormData();
  formData.append('photo', file);
  if (contributorName) formData.append('contributorName', contributorName);
  if (contributorEmail) formData.append('contributorEmail', contributorEmail);
  if (contributionId) formData.append('contributionId', contributionId);

  return publicUploadWithProgress(`/public/share/${token}/photo`, formData, onProgress);
};

// Retirer une photo qu'on vient d'envoyer (retour utilisateur, 2026-09-30) —
// jamais possible pour un autre envoi que le sien, voir la verification
// contributionId cote serveur.
export const deleteShareItem = (token, itemId, contributionId) => publicRequest(
  `/public/share/${token}/items/${itemId}`,
  { method: 'DELETE', body: JSON.stringify({ contributionId }) }
);

export const getApiBaseUrl = buildApiBaseUrl;

// --- Mode collectif (invitations nominatives, suivi, tracabilite) -----------
// Voir backend/routes/collective.js — coexiste avec le lien de partage
// anonyme ci-dessus (books.share_token) sans le remplacer : deux mecanismes
// independants. Cote proprietaire (authentifie, `request`) : activation,
// reglages, gestion des participants. Cote participant (public, par token
// individuel, `publicRequest`/`publicRawRequest`) : accueil + contribution.

export const activateCollective = (bookId, { organizerName, eventTitle, message, deadline, remindersEnabled, reminderDaysBefore }) => request(
  `/books/${bookId}/collective/activate`,
  { method: 'POST', body: JSON.stringify({ organizerName, eventTitle, message, deadline, remindersEnabled, reminderDaysBefore }) }
);

export const updateCollectiveSettings = (bookId, { organizerName, eventTitle, message, deadline, remindersEnabled, reminderDaysBefore }) => request(
  `/books/${bookId}/collective/settings`,
  { method: 'PUT', body: JSON.stringify({ organizerName, eventTitle, message, deadline, remindersEnabled, reminderDaysBefore }) }
);

export const fetchCollective = (bookId) => request(`/books/${bookId}/collective`);

export const addCollectiveParticipants = (bookId, emails, organizerName) => request(
  `/books/${bookId}/collective/participants`,
  { method: 'POST', body: JSON.stringify({ emails, organizerName }) }
);

export const updateCollectiveParticipant = (bookId, participantId, { email, name }) => request(
  `/books/${bookId}/collective/participants/${participantId}`,
  { method: 'PUT', body: JSON.stringify({ email, name }) }
);

// retirerPhotos : retire aussi ses photos NON placees dans le livre (refonte du collectif, phase 2). Renvoie { removedPhotos }.
export const deleteCollectiveParticipant = (bookId, participantId, { retirerPhotos = false } = {}) => request(
  `/books/${bookId}/collective/participants/${participantId}${retirerPhotos ? '?photos=retirer' : ''}`,
  { method: 'DELETE' }
);

export const remindCollectiveParticipant = (bookId, participantId) => request(
  `/books/${bookId}/collective/participants/${participantId}/remind`,
  { method: 'POST' }
);

export const fetchCollectiveParticipantContributions = (bookId, participantId) => request(
  `/books/${bookId}/collective/participants/${participantId}/contributions`
);

// --- Lien COMMUN du collectif (/invitation/:token, refonte phase 2) : la personne donne son prenom et recoit SON lien. ---
export const fetchCommonInvitation = (token) => publicRequest(`/public/invitation/${token}`);
export const joinCommonInvitation = (token, name) => publicRequest(
  `/public/invitation/${token}/join`,
  { method: 'POST', body: JSON.stringify({ name }) }
);

// Formulaire de contact du site (page FAQ, 2026-10-29). Une erreur 400 porte la liste des champs a corriger (err.fields).
export const sendContactMessage = async (valeurs) => {
  const response = await fetchWithRetry(`${buildApiBaseUrl()}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(valeurs)
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    const erreur = new Error(payload?.error || 'Erreur.');
    erreur.fields = payload?.fields || null;
    throw erreur;
  }
  return payload;
};

// --- Participant (public, par token individuel, sans compte) ---------------

export const fetchCollectiveInvite = (token) => publicRequest(`/public/collectif/${token}`);

export const submitCollectiveText = (token, text) => publicRequest(
  `/public/collectif/${token}/text`,
  { method: 'POST', body: JSON.stringify({ text }) }
);

export const submitCollectivePhoto = async (token, file, { onProgress } = {}) => {
  const formData = new FormData();
  formData.append('photo', file);
  return publicUploadWithProgress(`/public/collectif/${token}/photo`, formData, onProgress);
};

// Prenom de l'invite (2026-10-24) : affiche dans le livre (« Les contributeurs ») et dans l'e-mail au createur.
export const saveCollectiveName = (token, name) => publicRequest(
  `/public/collectif/${token}/name`,
  { method: 'PUT', body: JSON.stringify({ name }) }
);

// L'invite retire UN de ses envois pas encore place dans le livre (2026-10-29) ; le serveur reverifie tout.
export const deleteCollectiveItem = (token, itemId) => publicRequest(
  `/public/collectif/${token}/items/${encodeURIComponent(itemId)}`,
  { method: 'DELETE' }
);

export const finishCollectiveContribution = (token) => publicRequest(
  `/public/collectif/${token}/finish`,
  { method: 'POST' }
);
